// =============================================================================
// src/views/shared/ai-badge.ts — Badge "IA" visível por superfície + montagem.
//
// Por que existe: tornar EXPLÍCITO no front o que é gerado/processado por IA.
//   renderAiBadge(entry) é o chip único reusável (DRY); mountAiBadges injeta o chip
//   nos cabeçalhos das 5 áreas-IA (sidebar/chat/crise/reputação/monitoramento) em
//   call-time, idempotente — sem editar os literais gigantes dos takeovers. O
//   atributo data-ai-surface é o gancho greppável usado pelo /mapa-ia e pelos testes.
//
// O `status` espelha o idioma "real vs estimativa" (ELECTORAL_DATA_REAL): mostra
//   "ao vivo" (live), "parcial" (partial) ou "simulado" (mock).
// =============================================================================

import type { AiSurfaceEntry, AiSurfaceStatus } from '../../data/ai-surface-types';
import { aiSurfaceById } from '../../data/ai-surfaces';
import { qs, escapeHtml } from '../../app/dom';

/** Rótulo curto de cada status no chip. */
const AI_STATUS_LABEL: Record<AiSurfaceStatus, string> = {
  mock: 'simulado',
  partial: 'parcial',
  live: 'ao vivo',
};

/**
 * Chip "✨ IA · {status}" de UMA superfície. data-ai-surface = id greppável.
 *
 * @example
 *   header.insertAdjacentHTML('beforeend', renderAiBadge(entry));
 */
export function renderAiBadge(entry: AiSurfaceEntry): string {
  const status = AI_STATUS_LABEL[entry.status];
  const title = `${escapeHtml(entry.label)} · IA ${status}`;
  return (
    `<span class="u-ai-badge u-ai-badge--${entry.status}" data-ai-surface="${entry.id}" title="${title}">` +
    `<i class="ti ti-sparkles" aria-hidden="true"></i> IA · ${status}</span>`
  );
}

/** Onde montar um badge: seletor do cabeçalho ↔ id da superfície representativa. */
export interface AiBadgeMount {
  readonly selector: string;
  readonly surfaceId: string;
}

/**
 * Pontos de montagem dos badges visíveis — os 5 cabeçalhos das áreas-IA. Cada
 * surfaceId existe no registro (travado por registry-consistency.test.ts).
 */
export const AI_BADGE_MOUNTS: readonly AiBadgeMount[] = [
  { selector: '.copilot-head', surfaceId: 'copiloto.sidebar-context' },
  { selector: '.cop-head', surfaceId: 'copiloto.chat-narrative' },
  { selector: '.crz-head', surfaceId: 'crise.panorama' },
  { selector: '.rep-head', surfaceId: 'reputacao.index-gauge' },
  { selector: '#section-monitoramento .u-section-header', surfaceId: 'monitoramento.sentimento-kpi' },
];

/** Cabeçalho-alvo, ou null se ausente ou já com badge (idempotência). */
function resolveBadgeHost(mount: AiBadgeMount, root: ParentNode): HTMLElement | null {
  const host = qs<HTMLElement>(mount.selector, root);
  if (!host) return null;
  if (qs(`[data-ai-surface="${mount.surfaceId}"]`, host)) return null;
  return host;
}

/** Injeta o badge de UM ponto de montagem (no-op se host/entry ausente). */
export function mountAiBadgeAt(mount: AiBadgeMount, root: ParentNode = document): void {
  const host = resolveBadgeHost(mount, root);
  const entry = aiSurfaceById(mount.surfaceId);
  if (!host || !entry) return;
  host.insertAdjacentHTML('beforeend', renderAiBadge(entry));
}

/**
 * Injeta os badges nos cabeçalhos das áreas-IA presentes no DOM. Chamado uma vez
 * por main.ts após montar chrome + rotas (composition root, excluído da cobertura).
 */
export function mountAiBadges(root: ParentNode = document): void {
  for (const mount of AI_BADGE_MOUNTS) mountAiBadgeAt(mount, root);
}
